import type { InvoiceDto, PaymentDto } from '@aawas/contracts';
import clsx from 'clsx';
import { AlertTriangle, Banknote, Download, FileText, IndianRupee, Plus, ReceiptText, Settings2, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { downloadFile } from '../lib/api';
import { formatRupees, paiseToInput, parseRupees } from '../lib/money';
import {
  useChargeHeads,
  useCommunity,
  useCreateExpenseHead,
  useDues,
  useExpenseHeads,
  useMyMaintenance,
  useSetUnitAreas,
  useUnitAreas,
  useUpdateExpenseHead,
  useExpenses,
  useFinanceAccounts,
  useGenerateInvoices,
  useIncomeExpenditure,
  useInvoices,
  useLock,
  useMyFinance,
  usePayments,
  useRecordExpense,
  useRecordPayment,
  useReverseExpense,
  useReversePayment,
  useSetLock,
  useTrialBalance,
  useUnits,
  useUpdateChargeHead,
  useUpdateCommunity,
  useVoidInvoice,
  useVoidInvoices,
} from '../lib/queries';
import { Badge, Button, Card, Empty, ErrorNote, Field, Input, PageHeader, Segmented, Select, Sheet, Spinner, type Tone } from '../ui';
import { useToast } from '../ui/toast';
import { SpendingView } from './spending';

const FREQ_LABEL = { monthly: 'Monthly', quarterly: 'Quarterly', half_yearly: 'Half-yearly' } as const;
const FREQ_MONTHS = { monthly: 1, quarterly: 3, half_yearly: 6 } as const;
const FREQ_START_HINT = { monthly: '', quarterly: 'Quarterly bills start in April, July, October or January.', half_yearly: 'Half-yearly bills start in April or October.' } as const;
const startAllowed = (f: keyof typeof FREQ_MONTHS, month: string) => f === 'monthly' || (f === 'quarterly' ? [4, 7, 10, 1] : [4, 10]).includes(Number(month.slice(5, 7)));
const periodText = (period: string, months: number) => {
  const first = new Date(`${period.slice(0, 7)}-01T00:00:00`);
  const last = new Date(first.getFullYear(), first.getMonth() + months - 1, 1);
  const f = (d: Date, withYear: boolean) => d.toLocaleDateString('en-IN', { month: 'short', ...(withYear && { year: 'numeric' }) });
  return months === 1 ? f(first, true) : `${f(first, first.getFullYear() !== last.getFullYear())} – ${f(last, true)}`;
};

const todayIST = () => new Date().toLocaleDateString('en-CA', { timeZone: 'Asia/Kolkata' });
const monthNow = () => todayIST().slice(0, 7);
const fyStart = (d: string) => {
  const y = Number(d.slice(0, 4));
  return `${Number(d.slice(5, 7)) >= 4 ? y : y - 1}-04-01`;
};
const fmtDate = (d: string) => new Date(`${d}T00:00:00`).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' });
const monthLabel = (period: string) => new Date(`${period.slice(0, 7)}-01T00:00:00`).toLocaleDateString('en-IN', { month: 'long', year: 'numeric' });
const askReason = (what: string): string | null => {
  const r = window.prompt(`${what}\n\nWhy? (this is kept in the audit trail)`);
  return r && r.trim().length >= 3 ? r.trim() : null;
};
const MODE_LABEL: Record<string, string> = { upi: 'UPI', bank_transfer: 'Bank transfer', cheque: 'Cheque', cash: 'Cash', other: 'Other' };

function invoiceBadge(i: InvoiceDto): [string, Tone] {
  if (i.status === 'void') return ['Cancelled', 'gray'];
  if (i.status === 'paid') return ['Paid', 'green'];
  if (i.paidPaise > 0) return [i.overdue ? 'Part paid · overdue' : 'Part paid', i.overdue ? 'red' : 'amber'];
  return i.overdue ? ['Overdue', 'red'] : ['Unpaid', 'amber'];
}

// ───────────── committee: Finance ─────────────

type Tab = 'dues' | 'bills' | 'money' | 'spending' | 'setup';

export function FinanceScreen() {
  const heads = useChargeHeads();
  const [tab, setTab] = useState<Tab>('dues');
  const [payFor, setPayFor] = useState<{ open: boolean; unitId?: string }>({ open: false });
  const [expOpen, setExpOpen] = useState(false);
  const maint = heads.data?.[0];
  const notSetUp = !!maint && maint.amountPaise === 0;

  return (
    <div className="space-y-4">
      <PageHeader
        title="Finance"
        action={
          <div className="flex gap-2">
            <Button size="sm" onClick={() => setPayFor({ open: true })}><Plus className="h-4 w-4" /> Payment</Button>
            <Button size="sm" variant="secondary" onClick={() => setExpOpen(true)}><Plus className="h-4 w-4" /> Expense</Button>
          </div>
        }
      />
      {notSetUp && tab !== 'setup' && (
        <Card className="flex items-start gap-3 border-l-4 border-amber-400 bg-amber-50">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700" aria-hidden />
          <div className="flex-1 text-sm text-amber-950">
            <b>Start here:</b> enter the monthly maintenance amount your society charges each flat. Then you can send bills.
            <div className="mt-2"><Button size="sm" onClick={() => setTab('setup')}>Set the amount</Button></div>
          </div>
        </Card>
      )}
      <Segmented<Tab>
        value={tab}
        onChange={setTab}
        options={[
          { value: 'dues', label: 'Who owes' },
          { value: 'bills', label: 'Bills' },
          { value: 'money', label: 'Money' },
          { value: 'spending', label: 'Spending' },
          { value: 'setup', label: 'Setup' },
        ]}
      />

      {tab === 'dues' && <DuesTab onPay={(unitId) => setPayFor({ open: true, unitId })} />}
      {tab === 'bills' && <BillsTab />}
      {tab === 'money' && <MoneyTab />}
      {tab === 'spending' && <SpendingView />}
      {tab === 'setup' && <SetupTab />}

      <RecordPaymentSheet open={payFor.open} unitId={payFor.unitId} onClose={() => setPayFor({ open: false })} />
      <RecordExpenseSheet open={expOpen} onClose={() => setExpOpen(false)} />
    </div>
  );
}

// ───────────── Money: what came in and what went out ─────────────

function MoneyTab() {
  const [side, setSide] = useState<'in' | 'out'>('in');
  return (
    <div className="space-y-4">
      <Segmented<'in' | 'out'> value={side} onChange={setSide} options={[{ value: 'in', label: 'Received from flats' }, { value: 'out', label: 'Spent by society' }]} />
      {side === 'in' ? <PaymentsTab /> : <ExpensesTab />}
    </div>
  );
}

// ───────────── Dues ─────────────

function DuesTab({ onPay }: { onPay: (unitId: string) => void }) {
  const q = useDues();
  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const d = q.data;
  const rows = [...d.items].sort((a, b) => b.daysOverdue - a.daysOverdue || b.balancePaise - a.balancePaise);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <Card className="!p-3"><div className="text-xs text-slate-500">To collect</div><div className="text-lg font-bold tabular-nums">{formatRupees(d.totalDuePaise, { hideZeroFraction: true })}</div></Card>
        <Card className="!p-3"><div className="text-xs text-slate-500">Overdue flats</div><div className={clsx('text-lg font-bold', d.overdueUnits ? 'text-rose-700' : 'text-emerald-700')}>{d.overdueUnits}</div></Card>
        <Card className="!p-3"><div className="text-xs text-slate-500">Paid in advance</div><div className="text-lg font-bold tabular-nums">{formatRupees(d.totalAdvancePaise, { hideZeroFraction: true })}</div></Card>
      </div>
      {rows.length === 0 ? (
        <Empty icon={<IndianRupee className="h-8 w-8" />} title="No bills yet" hint="Once you send the first monthly bills, who owes what appears here." />
      ) : (
        <div className="space-y-2">
          {rows.map((r) => (
            <Card key={r.unitId} className="flex items-center justify-between gap-3 !p-3">
              <div className="min-w-0">
                <div className="font-semibold">Flat {r.unitLabel}</div>
                <div className="text-xs text-slate-500">
                  {r.balancePaise > 0 ? (r.daysOverdue > 0 ? `${r.daysOverdue} days overdue (since ${fmtDate(r.oldestUnpaidDue!)})` : `Due ${r.oldestUnpaidDue ? fmtDate(r.oldestUnpaidDue) : ''}`) : r.balancePaise < 0 ? 'Paid in advance' : 'All settled'}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className={clsx('font-bold tabular-nums', r.balancePaise > 0 ? (r.daysOverdue > 0 ? 'text-rose-700' : 'text-slate-900') : 'text-emerald-700')}>
                  {r.balancePaise < 0 ? `+${formatRupees(-r.balancePaise, { hideZeroFraction: true })}` : formatRupees(r.balancePaise, { hideZeroFraction: true })}
                </span>
                {r.balancePaise > 0 && <Button size="sm" variant="secondary" onClick={() => onPay(r.unitId)}>Record</Button>}
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

// ───────────── Bills ─────────────

function BillsTab() {
  const toast = useToast();
  const [month, setMonth] = useState(monthNow());
  const heads = useChargeHeads();
  const units = useUnits();
  const community = useCommunity();
  const gen = useGenerateInvoices();
  const invoices = useInvoices({});
  const voidInv = useVoidInvoice();
  const voidMany = useVoidInvoices();
  const frequency = community.data?.settings.billingFrequency ?? 'monthly';
  const months = FREQ_MONTHS[frequency];
  const active = heads.data?.filter((h) => h.active) ?? [];
  const perSqft = active.some((h) => h.basis === 'per_sqft');
  const allFlats = units.data ?? [];
  // null = every flat; otherwise only the ticked ones.
  const [picked, setPicked] = useState<Set<string> | null>(null);
  const [choosing, setChoosing] = useState(false);
  const chosen = picked ? allFlats.filter((u) => picked.has(u.id)) : allFlats;
  const flats = chosen.length;
  const noArea = perSqft ? chosen.filter((u) => u.areaSqft === null).length : 0;
  // What the chosen flats will be billed for one cycle (rounded per flat, as the server does).
  const cycleTotal = chosen.reduce((s, u) => s + active.reduce((t, h) => t + (h.basis === 'fixed' ? h.amountPaise * months : u.areaSqft === null ? 0 : Math.round(h.amountPaise * u.areaSqft * months)), 0), 0);
  const amount = active.reduce((s, h) => s + h.amountPaise, 0);
  const periodOk = startAllowed(frequency, month);
  const dueDay = community.data?.settings.invoiceDueDay ?? 10;
  const blocks = useMemo(() => [...new Set(allFlats.map((u) => u.blockName))], [allFlats]);

  function toggle(id: string) {
    const next = new Set(picked ?? allFlats.map((u) => u.id));
    next.has(id) ? next.delete(id) : next.add(id);
    setPicked(next.size === allFlats.length ? null : next);
  }
  function toggleBlock(block: string) {
    const ids = allFlats.filter((u) => u.blockName === block).map((u) => u.id);
    const next = new Set(picked ?? allFlats.map((u) => u.id));
    const allOn = ids.every((id) => next.has(id));
    ids.forEach((id) => (allOn ? next.delete(id) : next.add(id)));
    setPicked(next.size === allFlats.length ? null : next);
  }

  // Default due date: the society's due day; if that has already passed, one week from today.
  const defaultDue = useMemo(() => {
    const configured = `${month}-${String(dueDay).padStart(2, '0')}`;
    const today = todayIST();
    return configured >= today ? configured : new Date(Date.parse(today) + 7 * 86_400_000).toISOString().slice(0, 10);
  }, [month, dueDay]);
  const [dueEdited, setDueEdited] = useState<string | null>(null);
  const due = dueEdited ?? defaultDue;

  async function generate() {
    const who = picked ? `${flats} of ${allFlats.length} flats` : `all ${flats} flats`;
    if (!window.confirm(`Send ${periodText(month, months)} bills to ${who} (${formatRupees(cycleTotal, { hideZeroFraction: true })} in all), due ${fmtDate(due)}?\n\nResidents will be notified. Flats that already have a bill covering this period are skipped.`)) return;
    try {
      const r = await gen.mutateAsync({ period: month, dueDate: due, ...(picked && { unitIds: chosen.map((u) => u.id) }) });
      toast.success(r.created ? `${r.created} bills created${r.skippedExisting ? `, ${r.skippedExisting} already existed` : ''}` : 'Every chosen flat already has a bill for this period');
    } catch (e) {
      toast.error(e);
    }
  }

  function deleteOne(i: InvoiceDto) {
    const r = askReason(`Delete bill ${i.number} for flat ${i.unitLabel}? The resident will no longer owe it. It stays in the books as a cancelled entry.`);
    if (r) voidInv.mutate({ id: i.id, reason: r }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Bill deleted') });
  }
  function deleteUnpaid(list: InvoiceDto[], title: string) {
    const targets = list.filter((i) => i.status === 'open' && i.paidPaise === 0);
    if (targets.length === 0) return;
    const r = askReason(`Delete ${targets.length} unpaid bill${targets.length > 1 ? 's' : ''} for ${title}? Bills with any payment are kept.`);
    if (r) voidMany.mutate({ ids: targets.map((i) => i.id), reason: r }, { onError: (e) => toast.error(e), onSuccess: (x) => toast.success(`${x.voided} bill${x.voided === 1 ? '' : 's'} deleted`) });
  }

  const grouped = useMemo(() => {
    const m = new Map<string, InvoiceDto[]>();
    for (const i of invoices.data ?? []) if (i.status !== 'void') m.set(i.period, [...(m.get(i.period) ?? []), i]);
    return [...m.entries()];
  }, [invoices.data]);

  return (
    <div className="space-y-4">
      <Card className="space-y-3">
        <h2 className="font-semibold">Send {FREQ_LABEL[frequency].toLowerCase()} bills</h2>
        <div className="grid grid-cols-2 gap-3">
          <Field label={months === 1 ? 'Month' : 'Starting month'} hint={FREQ_START_HINT[frequency] || undefined} error={periodOk ? undefined : FREQ_START_HINT[frequency]}><Input type="month" value={month} onChange={(e) => { setMonth(e.target.value); setDueEdited(null); }} /></Field>
          <Field label="Due date" hint={dueEdited ? undefined : `Usually the ${dueDay}th; a week from today if that has passed`}><Input type="date" value={due} onChange={(e) => setDueEdited(e.target.value)} /></Field>
        </div>

        <div className="rounded-xl bg-slate-50 p-3">
          <div className="flex items-center justify-between gap-2 text-sm">
            <span>Send to: <b>{picked ? `${flats} of ${allFlats.length} flats` : `all ${allFlats.length} flats`}</b></span>
            <button type="button" className="font-semibold text-brand-700" onClick={() => setChoosing((v) => !v)}>{choosing ? 'Done' : 'Choose flats'}</button>
          </div>
          {choosing && (
            <div className="mt-3 space-y-3">
              <div className="flex flex-wrap gap-2">
                <button type="button" className="rounded-full bg-white px-3 py-1 text-xs font-semibold ring-1 ring-slate-300" onClick={() => setPicked(null)}>All flats</button>
                <button type="button" className="rounded-full bg-white px-3 py-1 text-xs font-semibold ring-1 ring-slate-300" onClick={() => setPicked(new Set())}>None</button>
                {blocks.length > 1 && blocks.map((b) => (
                  <button key={b} type="button" className="rounded-full bg-white px-3 py-1 text-xs font-semibold ring-1 ring-slate-300" onClick={() => toggleBlock(b)}>Block {b}</button>
                ))}
              </div>
              <div className="flex max-h-56 flex-wrap gap-2 overflow-y-auto">
                {allFlats.map((u) => {
                  const on = !picked || picked.has(u.id);
                  return (
                    <button key={u.id} type="button" aria-pressed={on} onClick={() => toggle(u.id)} className={clsx('rounded-lg px-3 py-1.5 text-sm font-semibold ring-1', on ? 'bg-brand-600 text-white ring-brand-600' : 'bg-white text-slate-600 ring-slate-300')}>{u.label}</button>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {amount > 0 ? (
          <p className="text-sm text-slate-700">{periodText(month, months)}: {flats} flat{flats === 1 ? '' : 's'}, <b>{formatRupees(cycleTotal, { hideZeroFraction: true })}</b> in all{perSqft ? ' (each flat by its area)' : ''}. Due <b>{fmtDate(due)}</b>.</p>
        ) : (
          <p className="text-sm text-amber-800">Set the maintenance amount in <b>Setup</b> first.</p>
        )}
        {noArea > 0 && <p className="text-sm text-amber-800">{noArea} chosen flat{noArea > 1 ? 's have' : ' has'} no area yet. Enter it in <b>Setup → Flat areas</b> before sending.</p>}
        <Button onClick={generate} loading={gen.isPending} disabled={amount === 0 || !month || !periodOk || noArea > 0 || flats === 0}><FileText className="h-4 w-4" /> Send bills{picked ? ` to ${flats} flat${flats === 1 ? '' : 's'}` : ''}</Button>
      </Card>

      {invoices.isLoading ? <Spinner /> : invoices.error ? <ErrorNote error={invoices.error} retry={() => invoices.refetch()} /> : grouped.length === 0 ? (
        <Empty icon={<FileText className="h-8 w-8" />} title="No bills sent yet" />
      ) : (
        grouped.map(([period, list]) => {
          const title = periodText(period, list[0]?.periodMonths ?? 1);
          const deletable = list.filter((i) => i.status === 'open' && i.paidPaise === 0).length;
          return (
            <section key={period} className="space-y-2">
              <div className="flex items-baseline justify-between gap-2">
                <h3 className="font-semibold">{title}</h3>
                <span className="text-sm text-slate-600">{list.filter((i) => i.status === 'paid').length}/{list.length} paid · {formatRupees(list.reduce((s, i) => s + i.balancePaise, 0), { hideZeroFraction: true })} pending</span>
              </div>
              {deletable > 1 && <button type="button" className="text-xs font-semibold text-rose-700" onClick={() => deleteUnpaid(list, title)}>Delete all {deletable} unpaid bills of this period</button>}
              {list.map((i) => {
                const [label, tone] = invoiceBadge(i);
                return (
                  <Card key={i.id} className="flex items-center justify-between gap-3 !p-3">
                    <div className="min-w-0">
                      <div className="font-semibold">Flat {i.unitLabel}</div>
                      <div className="text-xs text-slate-500">{i.number} · due {fmtDate(i.dueDate)}</div>
                    </div>
                    <div className="flex items-center gap-2">
                      <div className="text-right"><div className="font-semibold tabular-nums">{formatRupees(i.totalPaise, { hideZeroFraction: true })}</div><Badge tone={tone}>{label}</Badge></div>
                      {i.paidPaise === 0 ? (
                        <button aria-label={`Delete bill ${i.number}`} className="rounded-full p-2 text-slate-400 hover:bg-rose-50 hover:text-rose-700" onClick={() => deleteOne(i)}><Trash2 className="h-4 w-4" /></button>
                      ) : (
                        <button aria-label={`Why can't bill ${i.number} be deleted`} className="rounded-full p-2 text-slate-300" onClick={() => window.alert('This bill has a payment against it. Reverse the payment in the Payments tab first, then delete the bill.')}><Trash2 className="h-4 w-4" /></button>
                      )}
                    </div>
                  </Card>
                );
              })}
            </section>
          );
        })
      )}
    </div>
  );
}

// ───────────── Payments ─────────────

function PaymentsTab() {
  const toast = useToast();
  const q = usePayments();
  const reverse = useReversePayment();
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const list = q.data ?? [];
  if (list.length === 0) return <Empty icon={<ReceiptText className="h-8 w-8" />} title="No payments recorded" hint="When a resident pays by bank or UPI, tap “+ Payment” to record it and issue a receipt." />;
  return (
    <div className="space-y-2">
      {list.map((p) => (
        <Card key={p.id} className={clsx('!p-3', p.status === 'reversed' && 'opacity-60')}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold">Flat {p.unitLabel}{p.payerName ? ` · ${p.payerName}` : ''}</div>
              <div className="text-xs text-slate-500">{p.receiptNumber} · {MODE_LABEL[p.mode]}{p.reference ? ` · ${p.reference}` : ''} · {fmtDate(p.receivedOn)}</div>
              {p.status === 'recorded' && p.advancePaise > 0 && <div className="mt-1 text-xs text-emerald-700">{formatRupees(p.advancePaise, { hideZeroFraction: true })} held as advance</div>}
            </div>
            <div className="text-right">
              <div className="font-bold tabular-nums">{formatRupees(p.amountPaise, { hideZeroFraction: true })}</div>
              {p.status === 'reversed' ? <Badge tone="gray">Reversed</Badge> : (
                <button className="mt-1 text-xs font-semibold text-rose-700" onClick={() => { const r = askReason(`Reverse receipt ${p.receiptNumber} (${formatRupees(p.amountPaise)})?`); if (r) reverse.mutate({ id: p.id, reason: r }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Payment reversed') }); }}>Reverse</button>
              )}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function RecordPaymentSheet({ open, onClose, unitId }: { open: boolean; onClose: () => void; unitId?: string }) {
  const toast = useToast();
  const units = useUnits();
  const record = useRecordPayment();
  const [unit, setUnit] = useState('');
  const [amount, setAmount] = useState('');
  const [mode, setMode] = useState('upi');
  const [date, setDate] = useState(todayIST());
  const [ref, setRef] = useState('');
  const [payer, setPayer] = useState('');
  const [done, setDone] = useState<PaymentDto | null>(null);
  useEffect(() => { if (open) { setUnit(unitId ?? ''); setDone(null); setDate(todayIST()); } }, [open, unitId]);

  const paise = parseRupees(amount);
  const needsRef = mode !== 'cash';
  const valid = !!unit && paise !== null && paise > 0 && (!needsRef || ref.trim().length >= 3);

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || paise === null) return;
    try {
      const p = await record.mutateAsync({ unitId: unit, amountPaise: paise, mode, receivedOn: date, ...(ref.trim() && { reference: ref.trim() }), ...(payer.trim() && { payerName: payer.trim() }) });
      setDone(p);
      setAmount(''); setRef(''); setPayer('');
    } catch (err) {
      toast.error(err);
    }
  }

  return (
    <Sheet open={open} onClose={onClose} title={done ? 'Payment recorded' : 'Record a payment'}>
      {done ? (
        <div className="space-y-4 text-center">
          <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-emerald-100 text-3xl">✓</div>
          <div><div className="text-2xl font-bold">{formatRupees(done.amountPaise)}</div><div className="text-slate-700">received from flat {done.unitLabel}</div></div>
          <div className="rounded-xl bg-white p-3 text-left text-sm ring-1 ring-slate-200">
            <div>Receipt <b>{done.receiptNumber}</b></div>
            <div>Settled against bills: <b>{formatRupees(done.allocatedPaise)}</b></div>
            {done.advancePaise > 0 && <div className="text-emerald-700">Held as advance for next bill: <b>{formatRupees(done.advancePaise)}</b></div>}
            <div className="mt-1 text-slate-500">The resident has been notified.</div>
          </div>
          <div className="grid grid-cols-2 gap-3"><Button variant="secondary" onClick={() => setDone(null)}>Record another</Button><Button onClick={onClose}>Done</Button></div>
        </div>
      ) : (
        <form onSubmit={submit} className="space-y-4">
          <Field label="Flat">
            <Select value={unit} onChange={(e) => setUnit(e.target.value)} required>
              <option value="">Choose a flat…</option>
              {units.data?.map((u) => <option key={u.id} value={u.id}>{u.label}</option>)}
            </Select>
          </Field>
          <Field label="Amount received (₹)" error={amount && paise === null ? 'Enter a valid amount, e.g. 2500 or 2500.50' : undefined}>
            <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="2500" autoFocus={!unitId} />
          </Field>
          <div className="grid grid-cols-2 gap-3">
            <Field label="How paid"><Select value={mode} onChange={(e) => setMode(e.target.value)}><option value="upi">UPI</option><option value="bank_transfer">Bank transfer</option><option value="cheque">Cheque</option><option value="cash">Cash</option><option value="other">Other</option></Select></Field>
            <Field label="Date received"><Input type="date" max={todayIST()} value={date} onChange={(e) => setDate(e.target.value)} required /></Field>
          </div>
          <Field label={mode === 'cheque' ? 'Cheque number' : 'UTR / transaction ID'} hint={needsRef ? 'Required. This stops the same payment being entered twice.' : 'Optional for cash'}>
            <Input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={60} />
          </Field>
          <Field label="Paid by (optional)"><Input value={payer} onChange={(e) => setPayer(e.target.value)} maxLength={80} /></Field>
          <Button type="submit" size="lg" className="w-full" loading={record.isPending} disabled={!valid}>Record payment</Button>
        </form>
      )}
    </Sheet>
  );
}

// ───────────── Expenses ─────────────

function ExpensesTab() {
  const toast = useToast();
  const q = useExpenses();
  const reverse = useReverseExpense();
  if (q.isLoading) return <Spinner />;
  if (q.error) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const list = q.data ?? [];
  if (list.length === 0) return <div className="space-y-3"><ExpenseHeadsCard /><Empty icon={<Banknote className="h-8 w-8" />} title="No expenses recorded" hint="Record what the society spends (security, cleaning, electricity…) with “+ Expense”." /></div>;
  return (
    <div className="space-y-2">
      <ExpenseHeadsCard />
      {list.map((e) => (
        <Card key={e.id} className={clsx('!p-3', e.status === 'reversed' && 'opacity-60')}>
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="font-semibold">{e.payee}</div>
              <div className="text-xs text-slate-500">{e.accountName} · {e.voucherNumber} · {fmtDate(e.expenseDate)}</div>
              <div className="text-xs text-slate-500">Paid from {e.paidFromName}{e.reference ? ` · ${e.reference}` : ''}</div>
            </div>
            <div className="text-right">
              <div className="font-bold tabular-nums">{formatRupees(e.amountPaise, { hideZeroFraction: true })}</div>
              {e.status === 'reversed' ? <Badge tone="gray">Reversed</Badge> : <button className="mt-1 text-xs font-semibold text-rose-700" onClick={() => { const r = askReason(`Reverse expense ${e.voucherNumber}?`); if (r) reverse.mutate({ id: e.id, reason: r }, { onError: (er) => toast.error(er), onSuccess: () => toast.success('Expense reversed') }); }}>Reverse</button>}
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

/** The categories expenses are filed under (and shown to residents under). The committee can add its own. */
function ExpenseHeadsCard() {
  const toast = useToast();
  const heads = useExpenseHeads();
  const create = useCreateExpenseHead();
  const update = useUpdateExpenseHead();
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const list = heads.data ?? [];

  async function add(e: FormEvent) {
    e.preventDefault();
    if (name.trim().length < 2) return;
    try { await create.mutateAsync(name.trim()); toast.success('Head added'); setName(''); } catch (err) { toast.error(err); }
  }

  return (
    <Card className="space-y-3 !p-3">
      <button className="flex w-full items-center justify-between text-left" onClick={() => setOpen(!open)} aria-expanded={open}>
        <span className="font-semibold">Expense heads <span className="text-sm font-normal text-slate-500">({list.filter((h) => h.active).length})</span></span>
        <span className="text-sm font-semibold text-brand-700">{open ? 'Hide' : 'Manage'}</span>
      </button>
      {open && (
        <>
          <p className="text-sm text-slate-600">Every expense is filed under one of these. Residents see spending by head, so keep the names plain.</p>
          <form onSubmit={add} className="flex gap-2">
            <Input aria-label="New head name" value={name} onChange={(e) => setName(e.target.value)} placeholder="New head, e.g. Clubhouse upkeep" maxLength={80} />
            <Button type="submit" loading={create.isPending} disabled={name.trim().length < 2}><Plus className="h-4 w-4" /> Add</Button>
          </form>
          <ul className="divide-y divide-slate-100 text-sm">
            {list.map((h) => (
              <li key={h.id} className={clsx('flex items-center justify-between gap-2 py-1.5', !h.active && 'opacity-50')}>
                <span>{h.name}{!h.isSystem && <span className="ml-2 text-xs text-slate-500">added by you</span>}</span>
                {!h.isSystem && (
                  <button className="text-xs font-semibold text-brand-700" onClick={() => update.mutate({ id: h.id, patch: { active: !h.active } }, { onError: (er) => toast.error(er) })}>{h.active ? 'Switch off' : 'Switch on'}</button>
                )}
              </li>
            ))}
          </ul>
        </>
      )}
    </Card>
  );
}

function RecordExpenseSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const toast = useToast();
  const accounts = useFinanceAccounts();
  const record = useRecordExpense();
  const [date, setDate] = useState(todayIST());
  const [category, setCategory] = useState('');
  const [from, setFrom] = useState('');
  const [amount, setAmount] = useState('');
  const [payee, setPayee] = useState('');
  const [ref, setRef] = useState('');
  useEffect(() => { if (open) setDate(todayIST()); }, [open]);
  const cats = (accounts.data ?? []).filter((a) => a.type === 'expense' && a.active);
  const banks = (accounts.data ?? []).filter((a) => a.isBank && a.active);
  useEffect(() => { if (!from && banks[0]) setFrom(banks.find((b) => b.code === '1002')?.id ?? banks[0].id); }, [banks, from]);
  const paise = parseRupees(amount);
  const valid = !!category && !!from && paise !== null && paise > 0 && payee.trim().length >= 2;

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!valid || paise === null) return;
    try {
      const r = await record.mutateAsync({ expenseDate: date, expenseAccountId: category, paidFromAccountId: from, amountPaise: paise, payee: payee.trim(), ...(ref.trim() && { reference: ref.trim() }) });
      toast.success(`Recorded ${r.voucherNumber}`);
      setAmount(''); setPayee(''); setRef(''); onClose();
    } catch (err) { toast.error(err); }
  }
  return (
    <Sheet open={open} onClose={onClose} title="Record an expense">
      <form onSubmit={submit} className="space-y-4">
        <Field label="What was it for?"><Select value={category} onChange={(e) => setCategory(e.target.value)} required><option value="">Choose…</option>{cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}</Select></Field>
        <Field label="Paid to"><Input value={payee} onChange={(e) => setPayee(e.target.value)} maxLength={120} placeholder="e.g. Safe Guard Services" /></Field>
        <Field label="Amount (₹)" error={amount && paise === null ? 'Enter a valid amount' : undefined}><Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="18000" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><Input type="date" max={todayIST()} value={date} onChange={(e) => setDate(e.target.value)} required /></Field>
          <Field label="Paid from"><Select value={from} onChange={(e) => setFrom(e.target.value)}>{banks.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}</Select></Field>
        </div>
        <Field label="Cheque / reference (optional)"><Input value={ref} onChange={(e) => setRef(e.target.value)} maxLength={60} /></Field>
        <Button type="submit" size="lg" className="w-full" loading={record.isPending} disabled={!valid}>Record expense</Button>
      </form>
    </Sheet>
  );
}

// ───────────── Reports ─────────────

function ReportsTab() {
  const toast = useToast();
  const [from, setFrom] = useState(fyStart(todayIST()));
  const [to, setTo] = useState(todayIST());
  const ie = useIncomeExpenditure(from, to);
  const tb = useTrialBalance(to);
  const [busy, setBusy] = useState<string | null>(null);

  async function dl(part: 'masters' | 'vouchers') {
    setBusy(part);
    try {
      await downloadFile(`/finance/export/tally?part=${part}&from=${from}&to=${to}`, `aawas-tally-${part}.xml`);
    } catch (e) { toast.error(e); } finally { setBusy(null); }
  }

  return (
    <div className="space-y-4">
      <Card className="grid grid-cols-2 gap-3">
        <Field label="From"><Input type="date" value={from} onChange={(e) => setFrom(e.target.value)} /></Field>
        <Field label="To"><Input type="date" max={todayIST()} value={to} onChange={(e) => setTo(e.target.value)} /></Field>
      </Card>

      <Card className="space-y-3">
        <h2 className="font-semibold">Income & expenditure</h2>
        {ie.isLoading ? <Spinner /> : ie.error || !ie.data ? <ErrorNote error={ie.error} /> : (
          <>
            <Lines title="Income" rows={ie.data.income} total={ie.data.totalIncomePaise} />
            <Lines title="Expenditure" rows={ie.data.expenses} total={ie.data.totalExpensePaise} />
            <div className={clsx('flex items-center justify-between rounded-xl px-3 py-2 font-semibold', ie.data.surplusPaise >= 0 ? 'bg-emerald-50 text-emerald-900' : 'bg-rose-50 text-rose-900')}>
              <span>{ie.data.surplusPaise >= 0 ? 'Surplus' : 'Deficit'}</span><span className="tabular-nums">{formatRupees(Math.abs(ie.data.surplusPaise))}</span>
            </div>
          </>
        )}
      </Card>

      <Card className="space-y-3">
        <div className="flex items-center justify-between"><h2 className="font-semibold">Trial balance <span className="text-sm font-normal text-slate-500">as of {fmtDate(to)}</span></h2>{tb.data && <Badge tone={tb.data.balanced ? 'green' : 'red'}>{tb.data.balanced ? 'Balanced' : 'NOT BALANCED'}</Badge>}</div>
        {tb.isLoading ? <Spinner /> : tb.error || !tb.data ? <ErrorNote error={tb.error} /> : tb.data.rows.length === 0 ? <p className="text-sm text-slate-500">No entries yet.</p> : (
          <div className="overflow-x-auto"><table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-slate-500"><th className="py-1">Account</th><th className="py-1 text-right">Debit</th><th className="py-1 text-right">Credit</th></tr></thead>
            <tbody>{tb.data.rows.map((r) => <tr key={r.code} className="border-t border-slate-100"><td className="py-1.5">{r.code} · {r.name}</td><td className="py-1.5 text-right tabular-nums">{r.debitPaise ? formatRupees(r.debitPaise) : ''}</td><td className="py-1.5 text-right tabular-nums">{r.creditPaise ? formatRupees(r.creditPaise) : ''}</td></tr>)}</tbody>
            <tfoot><tr className="border-t-2 border-slate-300 font-semibold"><td className="py-1.5">Total</td><td className="py-1.5 text-right tabular-nums">{formatRupees(tb.data.totalDebitPaise)}</td><td className="py-1.5 text-right tabular-nums">{formatRupees(tb.data.totalCreditPaise)}</td></tr></tfoot>
          </table></div>
        )}
      </Card>

      <Card className="space-y-3">
        <h2 className="font-semibold">Export to Tally</h2>
        <p className="text-sm text-slate-700">Two files for your accountant: first import the <b>ledgers</b>, then the <b>vouchers</b> for the dates above.</p>
        <div className="rounded-xl bg-amber-50 px-3 py-2 text-sm text-amber-900"><b>Test first.</b> This export has not been tried in a real Tally yet. Import it into a practice company and check the balances before using it for the real books.</div>
        <div className="grid grid-cols-2 gap-3">
          <Button variant="secondary" loading={busy === 'masters'} onClick={() => dl('masters')}><Download className="h-4 w-4" /> 1. Ledgers</Button>
          <Button variant="secondary" loading={busy === 'vouchers'} onClick={() => dl('vouchers')}><Download className="h-4 w-4" /> 2. Vouchers</Button>
        </div>
      </Card>
    </div>
  );
}

function Lines({ title, rows, total }: { title: string; rows: Array<{ code: string; name: string; amountPaise: number }>; total: number }) {
  return (
    <div>
      <div className="mb-1 text-xs font-bold uppercase tracking-wide text-slate-500">{title}</div>
      {rows.length === 0 ? <div className="text-sm text-slate-400">Nothing in this period</div> : rows.map((r) => <div key={r.code} className="flex justify-between py-0.5 text-sm"><span>{r.name}</span><span className="tabular-nums">{formatRupees(r.amountPaise)}</span></div>)}
      <div className="mt-1 flex justify-between border-t border-slate-200 pt-1 text-sm font-semibold"><span>Total {title.toLowerCase()}</span><span className="tabular-nums">{formatRupees(total)}</span></div>
    </div>
  );
}

// ───────────── Setup: the committee decides these ─────────────

function SetupTab() {
  const toast = useToast();
  const heads = useChargeHeads();
  const units = useUnits();
  const community = useCommunity();
  const updateHead = useUpdateChargeHead();
  const updateCommunity = useUpdateCommunity();
  const lock = useLock();
  const setLock = useSetLock();
  const head = heads.data?.[0];
  const [amount, setAmount] = useState('');
  const [basis, setBasis] = useState<'fixed' | 'per_sqft'>('fixed');
  const [active, setActive] = useState(true);
  const [company, setCompany] = useState('');
  const [lockDate, setLockDate] = useState('');
  useEffect(() => { if (head) { setAmount(paiseToInput(head.amountPaise)); setBasis(head.basis); setActive(head.active || head.amountPaise === 0); } }, [head?.id, head?.amountPaise, head?.active, head?.basis]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => { if (community.data) setCompany(community.data.settings.tallyCompanyName); }, [community.data?.settings.tallyCompanyName]); // eslint-disable-line react-hooks/exhaustive-deps

  if (heads.isLoading || community.isLoading) return <Spinner />;
  if (!head || !community.data) return <ErrorNote error={heads.error ?? community.error ?? new Error('Finance is not set up for this society')} />;

  const paise = parseRupees(amount);
  const flats = units.data?.length ?? 0;
  const areaList = (units.data ?? []).map((u) => u.areaSqft);
  const totalArea = areaList.reduce<number>((t, a) => t + (a ?? 0), 0);
  const missingAreas = areaList.filter((a) => a === null).length;
  const s = community.data.settings;

  return (
    <div className="space-y-4">
      <Card className="space-y-4">
        <div><h2 className="font-semibold">Maintenance</h2><p className="text-sm text-slate-600">How much each flat pays. You can change it any time; bills already sent keep their amount.</p></div>
        <Segmented<'fixed' | 'per_sqft'>
          value={basis}
          onChange={setBasis}
          options={[{ value: 'fixed', label: 'Fixed per flat' }, { value: 'per_sqft', label: 'Per sq ft' }]}
        />
        <Field
          label={basis === 'fixed' ? 'Amount per flat (₹ per month)' : 'Rate (₹ per sq ft per month)'}
          hint={basis === 'per_sqft' ? 'Each flat pays rate × its area. Residents see the working on their own dashboard.' : undefined}
          error={amount && paise === null ? `Enter a valid amount, e.g. ${basis === 'fixed' ? '2500' : '2.50'}` : undefined}
        >
          <Input inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder={basis === 'fixed' ? '2500' : '2.50'} />
        </Field>
        <label className="flex items-center gap-3"><input type="checkbox" className="h-5 w-5" checked={active} onChange={(e) => setActive(e.target.checked)} /> Include this in bills</label>
        {paise !== null && paise > 0 && flats > 0 && basis === 'fixed' && <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-900">{flats} flats × {formatRupees(paise, { hideZeroFraction: true })} = <b>{formatRupees(paise * flats, { hideZeroFraction: true })}</b> per month</p>}
        {paise !== null && paise > 0 && basis === 'per_sqft' && <p className="rounded-xl bg-brand-50 px-3 py-2 text-sm text-brand-900">{formatRupees(paise)} × {totalArea.toLocaleString('en-IN')} sq ft (all flats) = <b>{formatRupees(Math.round(paise * totalArea), { hideZeroFraction: true })}</b> per month{missingAreas > 0 ? ` · ${missingAreas} flat${missingAreas > 1 ? 's' : ''} still need an area` : ''}</p>}
        <Field label="How often do you collect maintenance?" hint="Applies to the next bills you send. Bills already sent are not changed.">
          <Select value={s.billingFrequency} onChange={(e) => updateCommunity.mutate({ settings: { billingFrequency: e.target.value as 'monthly' | 'quarterly' | 'half_yearly' } }, { onError: (er) => toast.error(er), onSuccess: () => toast.success('Saved') })}>
            {(Object.keys(FREQ_LABEL) as Array<keyof typeof FREQ_LABEL>).map((f) => <option key={f} value={f}>{FREQ_LABEL[f]}</option>)}
          </Select>
        </Field>
        <Field label="Bills fall due on day" hint="Day of the month. Limited to 1–28 so it exists in every month.">
          <Select value={s.invoiceDueDay} onChange={(e) => updateCommunity.mutate({ settings: { invoiceDueDay: Number(e.target.value) } }, { onError: (er) => toast.error(er), onSuccess: () => toast.success('Saved') })}>
            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => <option key={d} value={d}>{d}</option>)}
          </Select>
        </Field>
        <Button loading={updateHead.isPending} disabled={paise === null || (paise === 0 && active)} onClick={() => updateHead.mutate({ id: head.id, patch: { basis, amountPaise: paise!, active: active && paise! > 0 } }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Saved') })}>Save</Button>
      </Card>

      {basis === 'per_sqft' && <FlatAreasCard />}

      <details className="rounded-2xl bg-white ring-1 ring-slate-200">
        <summary className="cursor-pointer list-none px-4 py-3 font-semibold">For your accountant <span className="text-sm font-normal text-slate-500">· reports, Tally, close the books</span></summary>
        <div className="space-y-4 px-4 pb-4">
          <ReportsTab />
      <Card className="space-y-3">
        <div><h2 className="font-semibold">Tally</h2><p className="text-sm text-slate-600">The company name in your accountant's Tally. It must match exactly, including capitals and spaces.</p></div>
        <Field label="Tally company name"><Input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={100} /></Field>
        <Button variant="secondary" loading={updateCommunity.isPending} disabled={!company.trim() || company.trim() === s.tallyCompanyName} onClick={() => updateCommunity.mutate({ settings: { tallyCompanyName: company.trim() } }, { onError: (e) => toast.error(e), onSuccess: () => toast.success('Saved') })}>Save company name</Button>
      </Card>

      <Card className="space-y-3">
        <div><h2 className="font-semibold">Close the books</h2><p className="text-sm text-slate-600">After your auditor signs off a period, close it. Nothing dated on or before that day can be added or changed afterwards, and it <b>cannot be reopened</b>.</p></div>
        <p className="text-sm">{lock.data?.lockedThrough ? <>Books are closed through <b>{fmtDate(lock.data.lockedThrough)}</b>.</> : 'No period is closed yet.'}</p>
        <Field label="Close everything up to and including"><Input type="date" max={todayIST()} value={lockDate} onChange={(e) => setLockDate(e.target.value)} /></Field>
        <Button variant="danger" loading={setLock.isPending} disabled={!lockDate} onClick={() => { if (window.confirm(`Close the books through ${fmtDate(lockDate)}?\n\nThis cannot be undone.`)) setLock.mutate(lockDate, { onError: (e) => toast.error(e), onSuccess: () => { toast.success('Books closed'); setLockDate(''); } }); }}>Close books</Button>
      </Card>
        </div>
      </details>
      <p className="flex items-center gap-2 text-xs text-slate-500"><Settings2 className="h-4 w-4" aria-hidden /> Changes here are recorded in the audit trail.</p>
    </div>
  );
}

/** Each flat's area in sq ft. Needed for per-sq-ft maintenance. */
function FlatAreasCard() {
  const toast = useToast();
  const areas = useUnitAreas();
  const save = useSetUnitAreas();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [all, setAll] = useState('');
  const list = areas.data ?? [];
  const valueOf = (u: { unitId: string; areaSqft: number | null }) => draft[u.unitId] ?? (u.areaSqft === null ? '' : String(u.areaSqft));
  const parse = (t: string): number | null | 'bad' => (t.trim() === '' ? null : /^\d+(\.\d{1,2})?$/.test(t.trim()) && Number(t) > 0 ? Number(t) : 'bad');
  const changed = list.filter((u) => draft[u.unitId] !== undefined && parse(draft[u.unitId]!) !== u.areaSqft);
  const bad = changed.some((u) => parse(draft[u.unitId]!) === 'bad');

  if (areas.isLoading) return <Spinner />;
  if (areas.error) return <ErrorNote error={areas.error} retry={() => areas.refetch()} />;

  async function submit() {
    try {
      await save.mutateAsync(changed.map((u) => ({ unitId: u.unitId, areaSqft: parse(draft[u.unitId]!) as number | null })));
      toast.success(`${changed.length} flat${changed.length > 1 ? 's' : ''} saved`);
      setDraft({});
    } catch (e) { toast.error(e); }
  }

  return (
    <Card className="space-y-3">
      <div><h2 className="font-semibold">Flat areas (sq ft)</h2><p className="text-sm text-slate-600">{list.filter((u) => u.areaSqft === null).length} of {list.length} flats still need an area. Bills cannot be sent until every flat has one.</p></div>
      <div className="flex gap-2">
        <Input aria-label="Same area for every flat" inputMode="decimal" value={all} onChange={(e) => setAll(e.target.value)} placeholder="Same for all flats, e.g. 1100" />
        <Button variant="secondary" disabled={parse(all) === null || parse(all) === 'bad'} onClick={() => { setDraft(Object.fromEntries(list.map((u) => [u.unitId, all.trim()]))); }}>Fill all</Button>
      </div>
      <div className="max-h-96 divide-y divide-slate-100 overflow-y-auto">
        {list.map((u) => {
          const v = valueOf(u);
          return (
            <label key={u.unitId} className="flex items-center justify-between gap-3 py-1.5 text-sm">
              <span className="font-semibold">Flat {u.unitLabel}</span>
              <Input aria-label={`Area of flat ${u.unitLabel}`} className="!w-32 text-right" inputMode="decimal" value={v} onChange={(e) => setDraft({ ...draft, [u.unitId]: e.target.value })} placeholder="sq ft" />
            </label>
          );
        })}
      </div>
      {bad && <p className="text-sm text-rose-700">Areas must be numbers above zero with at most 2 decimals.</p>}
      <Button loading={save.isPending} disabled={changed.length === 0 || bad} onClick={submit}>Save {changed.length > 0 ? `${changed.length} change${changed.length > 1 ? 's' : ''}` : 'areas'}</Button>
    </Card>
  );
}

// ───────────── resident: My dues ─────────────

/** "How is my maintenance worked out": the resident's flat area, rate and amount per month and per bill. */
function MaintenanceBreakdown() {
  const q = useMyMaintenance();
  if (!q.data || q.data.units.length === 0) return null;
  const { units, frequency, monthsPerCycle } = q.data;
  return (
    <>
      {units.map((u) => (
        <Card key={u.unitId} className="space-y-2">
          <div className="flex items-baseline justify-between"><h2 className="font-semibold">Your maintenance · Flat {u.unitLabel}</h2>{u.areaSqft !== null && <span className="text-sm text-slate-500">{u.areaSqft.toLocaleString('en-IN')} sq ft</span>}</div>
          {u.charges.length === 0 ? <p className="text-sm text-slate-600">Your society has not set the maintenance amount yet.</p> : (
            <>
              {u.charges.map((c) => (
                <div key={c.name} className="flex items-baseline justify-between gap-3 text-sm">
                  <span className="text-slate-700">{c.name}{c.basis === 'per_sqft' && <span className="text-slate-500"> · {u.areaSqft ?? '?'} sq ft × {formatRupees(c.ratePaise)}</span>}</span>
                  <span className="font-semibold tabular-nums">{c.perMonthPaise === null ? 'Area not entered' : `${formatRupees(c.perMonthPaise, { hideZeroFraction: true })}/month`}</span>
                </div>
              ))}
              {u.perMonthPaise !== null && (
                <div className="flex items-center justify-between rounded-xl bg-brand-50 px-3 py-2 font-semibold text-brand-900">
                  <span>{monthsPerCycle === 1 ? 'You pay each month' : `You pay ${FREQ_LABEL[frequency].toLowerCase()} (${monthsPerCycle} months)`}</span>
                  <span className="tabular-nums">{formatRupees(u.perCyclePaise!, { hideZeroFraction: true })}</span>
                </div>
              )}
              {u.perMonthPaise !== null && u.areaSqft !== null && u.areaSqft > 0 && <p className="text-sm text-slate-600">That is {formatRupees(u.perMonthPaise / u.areaSqft)} per sq ft per month.</p>}
              {u.perMonthPaise === null && <p className="text-sm text-amber-800">Ask the committee to enter your flat's area so your amount can be worked out.</p>}
            </>
          )}
        </Card>
      ))}
    </>
  );
}

export function MyDuesScreen() {
  const q = useMyFinance();
  if (q.isLoading) return <Spinner />;
  if (q.error || !q.data) return <ErrorNote error={q.error} retry={() => q.refetch()} />;
  const { balancePaise, invoices, payments } = q.data;
  const live = invoices.filter((i) => i.status !== 'void');
  return (
    <div className="space-y-4">
      <PageHeader title="Maintenance dues" />
      <Card className={clsx('text-center', balancePaise > 0 ? 'bg-amber-50 ring-amber-200' : 'bg-emerald-50 ring-emerald-200')}>
        <div className="text-sm text-slate-600">{balancePaise > 0 ? 'You owe' : balancePaise < 0 ? 'Paid in advance' : 'You are all settled'}</div>
        <div className={clsx('text-4xl font-bold tabular-nums', balancePaise > 0 ? 'text-amber-900' : 'text-emerald-800')}>{formatRupees(Math.abs(balancePaise))}</div>
        {balancePaise > 0 && <p className="mt-2 text-sm text-slate-700">Pay by bank or UPI transfer to your society's account and give the UTR / transaction ID to the committee. They will record it and you'll get a receipt notification.</p>}
      </Card>

      <MaintenanceBreakdown />

      <Link to="/spending">
        <Card className="flex items-center justify-between gap-3">
          <div><div className="font-semibold">Where does our maintenance go?</div><div className="text-sm text-slate-600">Society spending by head, month by month</div></div>
          <span className="text-sm font-semibold text-brand-700">View →</span>
        </Card>
      </Link>

      <section className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Bills</h2>
        {live.length === 0 ? <Empty title="No bills yet" /> : live.map((i) => {
          const [label, tone] = invoiceBadge(i);
          return (
            <Card key={i.id} className="flex items-center justify-between gap-3 !p-3">
              <div className="min-w-0"><div className="font-semibold">{periodText(i.period, i.periodMonths)}</div><div className="text-xs text-slate-500">{i.number} · due {fmtDate(i.dueDate)}</div>{i.lines.length > 0 && <div className="text-xs text-slate-500">{i.lines.map((l) => l.description).join(' · ')}</div>}</div>
              <div className="text-right"><div className="font-semibold tabular-nums">{formatRupees(i.totalPaise, { hideZeroFraction: true })}</div><Badge tone={tone}>{label}</Badge></div>
            </Card>
          );
        })}
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-bold uppercase tracking-wide text-slate-600">Payments received</h2>
        {payments.filter((p) => p.status === 'recorded').length === 0 ? <Empty title="No payments recorded yet" /> : payments.filter((p) => p.status === 'recorded').map((p) => (
          <Card key={p.id} className="flex items-center justify-between gap-3 !p-3">
            <div className="min-w-0"><div className="font-semibold">Receipt {p.receiptNumber}</div><div className="text-xs text-slate-500">{MODE_LABEL[p.mode]} · {fmtDate(p.receivedOn)}{p.reference ? ` · ${p.reference}` : ''}</div></div>
            <div className="font-semibold tabular-nums text-emerald-700">{formatRupees(p.amountPaise, { hideZeroFraction: true })}</div>
          </Card>
        ))}
      </section>
    </div>
  );
}

/** A compact card for the resident home screen. */
export function DuesCard() {
  const q = useMyFinance();
  const m = useMyMaintenance();
  if (!q.data) return null;
  const b = q.data.balancePaise;
  const cycle = m.data?.units.length === 1 ? m.data.units[0]! : null;
  if (q.data.invoices.length === 0 && cycle?.perCyclePaise == null) return null;
  return (
    <Link to="/dues">
      <Card className={clsx('flex items-center justify-between gap-3', b > 0 ? 'bg-amber-50 ring-amber-200' : '')}>
        <div>
          <div className="text-sm text-slate-600">Maintenance{cycle?.perCyclePaise != null && m.data ? ` · ${formatRupees(cycle.perCyclePaise, { hideZeroFraction: true })} ${m.data.monthsPerCycle === 1 ? 'a month' : m.data.frequency === 'quarterly' ? 'a quarter' : 'half-yearly'}` : ''}</div>
          <div className="font-bold">{q.data.invoices.length === 0 ? 'No bill yet' : b > 0 ? `${formatRupees(b, { hideZeroFraction: true })} due` : b < 0 ? `${formatRupees(-b, { hideZeroFraction: true })} advance` : 'All settled'}</div>
        </div>
        <span className="text-sm font-semibold text-brand-700">View →</span>
      </Card>
    </Link>
  );
}
